import {
  buildLocalSettings,
  getBaseSettings,
  WallMode,
  LocalSettings,
} from "./settings";
import {
  clearAllInteractionsCount,
  deleteDismissedData,
  getConsentData,
  getDismissData,
  getInteractionCountData,
  getRenderData,
  getSingleClickFlag,
  validateRecovery,
  invalidateUserData,
  ensureRecoveryType,
} from "./softwall/data";
import {
  getDismissHandler,
  onBounce,
  onPageView,
  onPremiumUserDetected,
  onRecover,
  onRecoverEvent,
} from "./softwall/handlers";
import { RLinkAPI } from "./api";
import {
  adsAllowed,
  detectAdBlocker,
  detectPremiumABUser,
  getAADetectionResult,
  isMobile,
  renderAllowed,
  validateHardWallState,
  logRlinkScriptLoaded,
} from "./softwall/utils";
import {
  log,
  setLogMetaKV,
  shouldLogByProbability,
} from "./softwall/utils/logs";
import { initializeRLinkIframe } from "./softwall/utils/iframeBridge";

import type {
  AdBlockerType,
  BeforeUnloadOption,
  DismissData,
} from "./softwall/types";

(async () => {
  // Prevent rlink script from running multiple times.
  // This can happen if it's injected along with bt_tag in 'inject from tag' mode,
  //   or due to a bug causing an infinite script loading loop.
  if (window.__bt_rlink_already_invoked) return;

  const loadTS = performance.now();
  logRlinkScriptLoaded();

  window.__bt_rlink_already_invoked = true;

  const isMobileDevice = isMobile();
  setLogMetaKV("version", __SCRIPT_VERSION__);
  setLogMetaKV("isMobile", isMobileDevice ? "true" : "false");

  // Start iframe initialization non-blockingly
  const iframeInitPromise = initializeRLinkIframe();

  try {
    let detectedExtension: AdBlockerType = "unknown";

    // Await iframe initialization *before* first potential storage access
    await iframeInitPromise;

    const [aaDetectionResults, consentData, baseSettings] = await Promise.all([
      getAADetectionResult(),
      getConsentData(),
      getBaseSettings(),
    ]);

    window.__bt_intrnl.consentData = consentData;
    const {
      enabled,
      allow_render_to_aa_users,
      attribution_window_days,
      wall_mode,
    } = baseSettings;

    // if consent were not given script should stop processing
    // all data previously stored in local storage should be invalidated
    if (!consentData.isConsentGiven) {
      await invalidateUserData();
      onPageView(
        enabled,
        undefined, // cannot define recovery state if consent declined
        aaDetectionResults,
        detectedExtension, // pass "unknown" extension if consent declined
        consentData.consentShownInCurrentPV,
        undefined, // cannot define attribution expiry if consent declined
      );
      return;
    }

    // RL should not be rendered and process any RL events if:
    // - ads already allowed (AB disabled or AA enabled and rendering not allowed for AA users)
    // - consent message appears in the same page view
    const shouldNotBeRendered =
      adsAllowed(aaDetectionResults, Boolean(allow_render_to_aa_users)) ||
      consentData.consentShownInCurrentPV;

    const hasRendered = await getRenderData(enabled);

    // Stop RL for first run if it should not be rendered
    if (!hasRendered && shouldNotBeRendered) {
      onPageView(
        enabled,
        undefined,
        aaDetectionResults,
        detectedExtension, // pass "unknown" extension if message should not be rendered
        consentData.consentShownInCurrentPV,
        undefined,
      );
      return;
    }

    // validate recovery
    const { swRecovered, attributionExpiry } = await validateRecovery(
      Boolean(allow_render_to_aa_users),
      Number(attribution_window_days),
      aaDetectionResults,
    );

    if (swRecovered && !isMobileDevice) {
      // trigger trackers
      onRecover(swRecovered, attributionExpiry, loadTS);
    }

    // get data that might be required for next processing
    const [premiumDetectionResult, dismissCount] = await Promise.all([
      detectPremiumABUser(baseSettings),
      getInteractionCountData("dismiss"),
    ]);

    // validate extension data
    const adblockers: Record<string, AdBlockerType> = {
      adblockplus: "abp",
      adblock: "ab",
    };

    if (
      premiumDetectionResult?.extensionInfo &&
      premiumDetectionResult.extensionInfo.name in adblockers
    ) {
      detectedExtension = adblockers[premiumDetectionResult.extensionInfo.name];
    } else {
      detectedExtension = await detectAdBlocker();
    }

    // ensure recovery type - is AB recovery was caused by SCA or manual allowlisting
    const allowlistingState =
      premiumDetectionResult?.extensionInfo?.allowlistState;
    const recoveryType = ensureRecoveryType(swRecovered, allowlistingState);

    onPageView(
      enabled,
      recoveryType,
      aaDetectionResults,
      detectedExtension,
      consentData.consentShownInCurrentPV,
      attributionExpiry,
    );

    // recovery event
    if (swRecovered && !isMobileDevice) {
      onRecoverEvent(recoveryType, attributionExpiry, detectedExtension).catch(
        (error) => {
          log(
            "rlink.onRecoverError",
            {
              aaResults: window.__bt_intrnl.aaDetectionResults,
              error: error instanceof Error ? error.message : error,
            },
            { severity: 4 },
          );
        },
      );

      if (shouldLogByProbability()) {
        // log SCA state only when recovered
        log(
          "rlink.scaState",
          {
            aaResults: window.__bt_intrnl.aaDetectionResults,
            scaFlag: getSingleClickFlag(),
            recoveryType: recoveryType,
            extAllowlistState: allowlistingState,
          },
          { severity: 1 },
        );
      }
      return;
    }

    const dynamicWallModeEnabled = wall_mode === WallMode.dynamic;

    // if we had Dynamic Wall before and change it to other type and change back
    // we need to avoid possible issues where we have some values in local storage
    if (!dynamicWallModeEnabled && dismissCount > 0) {
      // have to ensure that counters cleaned up before continue
      await clearAllInteractionsCount();
    }

    // we only need local settings if user not recovered yet
    const settings: LocalSettings = await buildLocalSettings(baseSettings);

    const beforeUnloadHandler = () => {
      onBounce(detectedExtension, settings);
    };

    const beforeUnload = (opt: BeforeUnloadOption) => {
      if (opt === "subscribe") {
        window.addEventListener("beforeunload", beforeUnloadHandler);
      } else {
        window.removeEventListener("beforeunload", beforeUnloadHandler);
      }
    };

    const dismissHandler = getDismissHandler(detectedExtension, settings);
    const onDismiss = async (dismissData: DismissData): Promise<void> => {
      beforeUnload("unsubscribe"); // if user dismiss the wall stop listen to beforeunload
      dismissHandler.onDismiss(dismissData);
    };

    // dismiss event
    const dismissData = await getDismissData();
    const isHardMode = validateHardWallState({
      settings,
      aaDetectionResults,
      dismissCount,
    });

    if (dismissData && !isHardMode) {
      onDismiss(dismissData);
      // we expect to send dismiss event if dismiss button has been clicked once per session
      // however if dismiss happen on dynamic wall mode we should continue processing
      // since message allowed to be rendered again within the same session
      // after specified number of page views happen
      if (!dynamicWallModeEnabled) {
        return;
      }
      // when switching to dynamic wall mode within the same session,
      // we need to invalidate the previous dismiss data stored in session storage.
      // This ensures the wall is rendered correctly, even if the user had previously dismissed it.
      if (dynamicWallModeEnabled && dismissCount <= 0) {
        await deleteDismissedData();
      }
    }

    // handle premium ABP users
    if (premiumDetectionResult?.isPremium) {
      onPremiumUserDetected(premiumDetectionResult, detectedExtension);
      return;
    }

    // render RL if the rules for rendering are met
    if (!shouldNotBeRendered) {
      renderAllowed(settings).then((allowed) => {
        if (!allowed) return;
        import("./App").then(({ render }) => {
          const renderProps = {
            settings,
            onDismiss,
            beforeUnload,
            detectedExtension,
            isHardMode,
          };
          const callRender = () => render(renderProps);
          if (settings.render_mode === "api") {
            new RLinkAPI(callRender, {
              mode: settings.api_mode,
            }).expose();
          } else {
            callRender();
          }
        });
      });
    }
  } catch (error) {
    log(
      "rlink.executionError",
      {
        aaResults: window.__bt_intrnl.aaDetectionResults,
        consent: window.__bt_intrnl.consentData,
        error: error instanceof Error ? error.message : String(error),
        ...(error?.cause && {
          cause:
            error.cause instanceof Error
              ? error.cause.message
              : String(error.cause),
        }),
      },
      { severity: 5 },
    );
    return;
  }
})();
